/**
 * ADR-165 B — Overlapping position legs.
 *
 * The rows the ADR-164 repair would act on (OPEN_OPEN: more than one open leg,
 * with the planned action or the reason it is manual) and the overlaps the
 * repair does not cover (CLOSED_OVERLAP: at least one leg closed). Each row
 * shows the contracts covering the overlap, which is what makes a case
 * readable.
 *
 * Laid out like Reports → Ingestion Sync Runs: the same filter bar, the
 * "N … found" count, the same table wrapper and two-line cells, and Load More.
 *
 * Read-only, except one deliberate action: "Repair this person" on rows the
 * repair can apply (CLOSE / REANCHOR / REALIGN). It shows the planned changes,
 * then runs the repair for that one person. There is no "repair all" here.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaWrench } from 'react-icons/fa';
import { HeaderButton } from '../../common/styles';
import EmployeeSearch from '../../common/EmployeeSearch';
import ClusterSelector from '../../common/ClusterSelector';
import { CLUSTER_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { REGION_STRUCTURE_TYPE_CODE } from '../../../constants/orgStructureTypes';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import { HeaderStats, HeaderStat } from '../EmploymentChangeLogReport/styles';
import {
  FilterBar,
  TableWrapper,
  TwoLine,
  TwoLinePrimary,
  TwoLineSecondary
} from '../DirectManagerDiscrepancies/styles';
import { LinkButton } from '../IngestionSyncRuns/styles';
// The Bench Candidates Load More button (owner, 2026-10-09).
import { LoadMoreButton } from '../BenchCandidates/styles';
import { getChipSx } from '../../../constants/chipPalette';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import Modal from '../../common/Modal';
import { useAlert } from '../../../contexts/AlertContext';
import { employmentApi } from '../../../services/api';
import {
  ACTION_LABELS,
  KIND_LABELS,
  PAGE_SIZE,
  contractLabel,
  describePlannedChange,
  filterRows,
  fmtDay,
  legRange,
  relevantContracts,
  rowReason
} from '../../../utils/overlappingPositionLegs';

const KIND_OPTIONS = Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }));

const SingleSelect = ({ id, label, value, options, onChange, minWidth = 160 }) => (
  <FormControl size="small" sx={{ minWidth }}>
    <InputLabel id={id}>{label}</InputLabel>
    <Select labelId={id} label={label} value={value || ''} onChange={(event) => onChange(event.target.value)}>
      <MenuItem value=""><em>All</em></MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const LegCell = ({ leg }) => (
  <TableCell>
    <TwoLine>
      <TwoLinePrimary>{leg.position || '—'}</TwoLinePrimary>
      <TwoLineSecondary>{legRange(leg)}</TwoLineSecondary>
    </TwoLine>
  </TableCell>
);

const FindingCell = ({ row }) => {
  const label = row.action ? (ACTION_LABELS[row.action] || row.action) : KIND_LABELS.CLOSED_OVERLAP;
  const reason = rowReason(row);
  return (
    <TableCell>
      <TwoLine>
        <span>
          <Chip size="small" label={label} sx={getChipSx(row.automatic ? 'success' : 'warning')} />
        </span>
        {reason && <TwoLineSecondary>{reason}</TwoLineSecondary>}
        {row.automatic && <TwoLineSecondary>{describePlannedChange(row).join(' · ')}</TwoLineSecondary>}
      </TwoLine>
    </TableCell>
  );
};

const ContractsCell = ({ row }) => {
  const { shown, hidden } = relevantContracts(row);
  return (
    <TableCell>
      <TwoLine>
        {shown.map((c) => (
          <TwoLineSecondary key={c.id} style={c.primary ? { fontWeight: 600 } : undefined}>{contractLabel(c)}</TwoLineSecondary>
        ))}
        {hidden.length > 0 && (
          <Tooltip arrow title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{hidden.map(contractLabel).join('\n')}</Box>}>
            <TwoLineSecondary style={{ cursor: 'help', textDecoration: 'underline dotted' }}>+{hidden.length} more</TwoLineSecondary>
          </Tooltip>
        )}
      </TwoLine>
    </TableCell>
  );
};

const REPORT_PATH = '/reports/overlapping-position-legs';
const DEFAULT_FILTERS = { kind: '', action: '', reason: '', regionId: '' };

const OverlappingPositionLegsReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  // Filters, rows shown and scroll are restored on return from an
  // individual's page (the ftcState / changeLogState pattern).
  const restored = location.state?.overlapState;
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...(restored?.filters || {}) });
  const [selectedEmployee, setSelectedEmployee] = useState(restored?.selectedEmployee || null);
  const [visible, setVisible] = useState(restored?.visible || PAGE_SIZE);
  const [repairRow, setRepairRow] = useState(null);
  const [isRepairing, setIsRepairing] = useState(false);
  const [repairError, setRepairError] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      setReport(await employmentApi.getOverlappingPositionLegsReport());
    } catch (err) {
      setLoadError(err.message || 'Failed to load the report');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Back on the row the user left from, once the rows are on screen.
  useEffect(() => {
    if (!isLoading && report && restored?.scrollY) {
      requestAnimationFrame(() => window.scrollTo(0, restored.scrollY));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, report]);

  const openIndividual = (path) => navigate(path, {
    state: {
      returnTo: REPORT_PATH,
      returnLabel: 'Overlapping Position Legs',
      overlapState: { filters, selectedEmployee, visible, scrollY: window.scrollY }
    }
  });

  const rows = useMemo(
    () => filterRows(report?.rows, { ...filters, employeeId: selectedEmployee?.id || '' }),
    [report, filters, selectedEmployee]
  );
  const actionOptions = useMemo(
    () => Object.keys(report?.summary?.byAction || {}).map((a) => ({ value: a, label: ACTION_LABELS[a] || a })),
    [report]
  );
  const reasonOptions = useMemo(
    () => Object.keys(report?.summary?.byReason || {}).map((r) => ({ value: r, label: r })),
    [report]
  );
  const setFilter = (next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setVisible(PAGE_SIZE);
  };

  const confirmRepair = async () => {
    if (!repairRow) return;
    setIsRepairing(true);
    setRepairError('');
    try {
      const result = await employmentApi.repairPositionLegsForEmployee(repairRow.employeeId);
      if (result?.errors?.length) {
        setRepairError(result.errors.map((e) => e.message).join('; '));
        return;
      }
      showAlert(`Repaired ${repairRow.fullName}`, 'success');
      setRepairRow(null);
      await load();
    } catch (err) {
      setRepairError(err.message || 'Repair failed');
    } finally {
      setIsRepairing(false);
    }
  };

  const summary = report?.summary;
  const pageRows = rows.slice(0, visible);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Overlapping Position Legs</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading && !summary ? <CircularProgress size={20} /> : summary && (
            <>
              <HeaderStat>
                <div className="top"><span className="label">People</span><span className="value">{summary.people}</span></div>
              </HeaderStat>
              <HeaderStat>
                <div className="top"><span className="label">Automatic</span><span className="value">{summary.automatic}</span></div>
                <span className="sub">Repair can fix</span>
              </HeaderStat>
              <HeaderStat $alarm $clear={summary.manual === 0}>
                <div className="top"><span className="label">Manual</span><span className="value">{summary.manual}</span></div>
                <span className="sub">Two open, by hand</span>
              </HeaderStat>
              <HeaderStat $alarm $clear={summary.closedOverlaps === 0}>
                <div className="top"><span className="label">Closed overlaps</span><span className="value">{summary.closedOverlaps}</span></div>
                <span className="sub">By hand</span>
              </HeaderStat>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection>
          <FilterBar>
            <SingleSelect id="opl-kind-label" label="Kind" value={filters.kind} options={KIND_OPTIONS}
              onChange={(value) => setFilter({ kind: value })} />
            <SingleSelect id="opl-action-label" label="Action" value={filters.action} options={actionOptions}
              onChange={(value) => setFilter({ action: value })} minWidth={200} />
            <SingleSelect id="opl-reason-label" label="Reason" value={filters.reason} options={reasonOptions}
              onChange={(value) => setFilter({ reason: value })} minWidth={260} />
            {/* The individual's Region — the same picker as the Individuals
                Repository "Regions" filter (ADR-119 amendment 1). */}
            <ClusterSelector
              value={filters.regionId}
              onChange={(value) => setFilter({ regionId: value || '' })}
              label="Region"
              allowSelectAll={true}
              allOptionLabel="All Regions"
              allOptionValue=""
              structureTypeCodes={[REGION_STRUCTURE_TYPE_CODE]}
              returnValue={CLUSTER_RETURN_TYPES.ID}
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />
            <EmployeeSearchProvider instanceId="overlapping-position-legs-search">
              <EmployeeSearch
                value={selectedEmployee}
                onChange={(employee) => { setSelectedEmployee(employee); setVisible(PAGE_SIZE); }}
                label="Individual"
                placeholder="Search individuals..."
                width={220}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>
          </FilterBar>

          {report?.errors?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {report.errors.length} individual(s) could not be planned: {report.errors.map((e) => e.message).join('; ')}
            </Alert>
          )}

          <ResultsCount count={rows.length} singular="overlap" plural="overlaps" />

          {isLoading && <LoadingSpinner />}
          {!isLoading && loadError && (
            <EmptyState title="Failed to load the report" description={loadError} />
          )}
          {!isLoading && !loadError && rows.length === 0 && (
            <EmptyState
              title={report?.rows?.length ? 'No overlaps match the filters' : 'No overlapping position legs'}
              description={report?.rows?.length ? 'Clear a filter to see more.' : 'Every individual holds at most one position at a time.'}
            />
          )}
          {!isLoading && !loadError && rows.length > 0 && (
            <>
              <TableWrapper>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Individual</TableCell>
                        <TableCell>Finding</TableCell>
                        <TableCell>Leg A</TableCell>
                        <TableCell>Leg B</TableCell>
                        <TableCell>Overlap</TableCell>
                        <TableCell>Contracts</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((r) => (
                        <TableRow key={`${r.kind}-${r.legA.id}-${r.legB.id}`} hover sx={{ verticalAlign: 'top' }}>
                          <TableCell>
                            <TwoLine>
                              <TwoLinePrimary>{r.fullName}</TwoLinePrimary>
                              {r.regionName && <TwoLineSecondary>{r.regionName}</TwoLineSecondary>}
                              <TwoLineSecondary>
                                <LinkButton type="button" onClick={() => openIndividual(`/employees/${r.employeeId}/positions`)}>Position history</LinkButton>
                                {' · '}
                                <LinkButton type="button" onClick={() => openIndividual(`/employees/${r.employeeId}/edit?tab=EMPLOYMENT_EPISODES`)}>Contracts</LinkButton>
                              </TwoLineSecondary>
                            </TwoLine>
                          </TableCell>
                          <FindingCell row={r} />
                          <LegCell leg={r.legA} />
                          <LegCell leg={r.legB} />
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDay(r.overlap.from)} – {fmtDay(r.overlap.to)}</TableCell>
                          <ContractsCell row={r} />
                          <TableCell align="right">
                            {r.automatic && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <HeaderButton type="button" onClick={() => { setRepairError(''); setRepairRow(r); }}>
                                  <FaWrench size={12} /> Repair
                                </HeaderButton>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableWrapper>
              {rows.length > visible && (
                <LoadMoreButton onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>

      {repairRow && (
        <Modal
          isOpen
          title={`Repair ${repairRow.fullName}`}
          onClose={() => setRepairRow(null)}
          onCancel={() => setRepairRow(null)}
          onSubmit={confirmRepair}
          submitText={isRepairing ? 'Repairing...' : 'Confirm repair'}
          submitDisabled={isRepairing}
          maxWidth="sm"
        >
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>These changes will be written ({ACTION_LABELS[repairRow.action]}):</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {describePlannedChange(repairRow).map((line) => <li key={line}><Typography variant="body2">{line}</Typography></li>)}
            </Box>
            <Typography variant="caption" component="div" sx={{ color: '#64748B', mt: 1.5 }}>
              Only this individual is repaired. The repair re-reads their positions when it runs, so reload the report first if someone has edited them since it loaded.
            </Typography>
            {repairError && <Alert severity="error" sx={{ mt: 2 }}>{repairError}</Alert>}
          </Box>
        </Modal>
      )}
    </Container>
  );
};

export default OverlappingPositionLegsReport;
